import React, { useState, useCallback, useEffect } from 'react';
import Layout from '../../components/Layout';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { exportCSV, csvDateStamp } from '../../utils/exportCSV';
import { useLicense } from '../../context/LicenseContext';
import { useToast } from '../../context/ToastContext';
import { useAudit } from '../../context/AuditContext';
import EmptyState from '../../components/EmptyState';

const JOBS_WIDGETS: PageWidget[] = [
  { id:'active',   label:'Active Jobs',      icon:'🔥', description:'Currently open jobs requiring action', defaultVisible:true },
  { id:'pending',  label:'Pending Review',   icon:'⏳', description:'Jobs awaiting supervisor sign-off', defaultVisible:true },
  { id:'all',      label:'All Jobs',         icon:'📋', description:'Complete job list with filters', defaultVisible:true },
  { id:'closed',   label:'Closed Jobs',      icon:'✓',  description:'Completed and resolved jobs', defaultVisible:true },
  { id:'summary',  label:'Morning Summary',  icon:'🌅', description:'Shift handover morning briefing', defaultVisible:true },
  { id:'tours',    label:'Guard Tours',      icon:'🚶', description:'Patrol routes and tour management', defaultVisible:true },
  { id:'handover',  label:'Shift Handover',   icon:'🔄', description:'End of shift sign-off and notes', defaultVisible:true },
  { id:'recurring',   label:'Recurring Jobs',   icon:'🔁', description:'Scheduled recurring job templates', defaultVisible:true },
  { id:'loneworker', label:'Lone Worker',      icon:'👷', description:'Lone worker monitoring and check-ins', defaultVisible:true },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const JOB_TYPES = [
  'Duress', 'Break In / Intrusion', 'Footage Export Request',
  'Perimeter Breach', 'Medical Emergency', 'Fire Alarm',
  'Suspicious Person / Loitering', 'Trespass', 'Property Damage',
  'Technical Fault', 'Welfare Check', 'Vehicle Incident',
  'Lost / Found Property', 'Access Control Issue', 'Other',
];

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'] as const;
type Priority = typeof PRIORITIES[number];

const SITES = ['Town Hall', 'Civic Centre', 'Aquatic Centre', 'Council Depot', 'Parramatta Square', 'River Walk'];
const OPERATORS = ['J. Okafor', 'S. Mitchell', 'J. Chen', 'M. Torres', 'R. Patel'];

type JobStatus = 'new' | 'assigned' | 'in_progress' | 'pending_review' | 'closed';

const PRIORITY_COLOR: Record<Priority, string> = {
  Critical: '#ef4444', High: '#f59e0b', Medium: '#3b82f6', Low: '#555555',
};
const STATUS_COLOR: Record<JobStatus, string> = {
  new: '#555555', assigned: '#a855f7', in_progress: '#f59e0b',
  pending_review: '#3b82f6', closed: '#22c55e',
};
const STATUS_LABEL: Record<JobStatus, string> = {
  new: 'New', assigned: 'Assigned', in_progress: 'In Progress',
  pending_review: 'Pending Review', closed: 'Closed',
};

interface Activity {
  time: string;
  operator: string;
  action: string;
  note?: string;
}

interface CostItem {
  id: string;
  description: string;
  qty: number;
  unitCost: number;
}

interface Job {
  id: string;
  number: string;
  type: string;
  priority: Priority;
  site: string;
  location: string;
  status: JobStatus;
  assignedTo?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  linkedAlarmId?: number;
  linkedAssetId?: string;
  linkedAssetName?: string;
  policeRef?: string;
  activity: Activity[];
  supervisorNotes?: string;
  costItems?: CostItem[];
  labourHours?: number;
}

let _seq = 6; // seeds occupy 0001-0006; new jobs start at 0007+
function nextJobNumber(): string {
  _seq++;
  return `JOB-${String(_seq).padStart(4, '0')}`;
}
/** Call after loading jobs from API to keep counter in sync */
function syncJobSeq(jobs: { number?: string }[]) {
  jobs.forEach(j => {
    const m = (j.number || '').match(/^JOB-(\d+)$/);
    if (m) { const n = parseInt(m[1], 10); if (n > _seq) _seq = n; }
  });
}
/** Preview the next job number without incrementing */
function peekNextJobNumber(): string {
  return `JOB-${String(_seq + 1).padStart(4, '0')}`;
}

function now(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
}

// ─── Seed data ────────────────────────────────────────────────────────────────

const SEED_JOBS: Job[] = [
  {
    id:'1', number:'JOB-0001', type:'Duress', priority:'Critical',
    site:'Town Hall', location:'Control Room', status:'in_progress',
    assignedTo:'J. Okafor', createdBy:'System (Alarm Auto)', createdAt:'08:41:22', updatedAt:'08:43:10',
    description:'Duress button activated in Control Room. Guard dispatched to location.',
    linkedAlarmId:1,
    activity:[
      { time:'08:41:22', operator:'System', action:'Job auto-created from alarm', note:'Alarm #1 — Duress Button activated, Town Hall Control Room' },
      { time:'08:41:45', operator:'J. Okafor', action:'Alarm acknowledged', note:'Reviewing cameras. No visible threat on entry or lobby feed.' },
      { time:'08:43:10', operator:'J. Okafor', action:'Updated', note:'Site supervisor (K. Nguyen) contacted. Guard dispatched. ETA 3 minutes.' },
    ],
  },
  {
    id:'2', number:'JOB-0002', type:'Break In / Intrusion', priority:'Critical',
    site:'Civic Centre', location:'Server Room B2', status:'in_progress',
    assignedTo:'S. Mitchell', createdBy:'System (Alarm Auto)', createdAt:'08:39:05', updatedAt:'08:44:30',
    description:'Door forced open — Server Room B2. Possible unauthorised entry. Police notified.',
    linkedAlarmId:2, policeRef:'NSW Police Event #2026-44890',
    activity:[
      { time:'08:39:05', operator:'System', action:'Job auto-created from alarm', note:'Alarm #2 — Door Forced Open, Civic Centre Server Room B2' },
      { time:'08:39:20', operator:'S. Mitchell', action:'Alarm acknowledged', note:'Camera confirms door forced open. No person visible on current feed.' },
      { time:'08:40:15', operator:'S. Mitchell', action:'Updated', note:'Police contacted. NSW Police Event #2026-44890. Est. response 8 minutes.' },
      { time:'08:44:30', operator:'S. Mitchell', action:'Updated', note:'Guard on scene. No intruder found. Door mechanism appears damaged/tampered.' },
    ],
  },
  {
    id:'3', number:'JOB-0003', type:'Footage Export Request', priority:'Medium',
    site:'Parramatta Square', location:'Entry Plaza', status:'pending_review',
    assignedTo:'J. Chen', createdBy:'J. Chen', createdAt:'08:15:00', updatedAt:'08:50:20',
    description:'Police CCTV request — 9 Apr 2026 22:00–23:00. Entry Plaza. NSW Police Event #2026-44821. Sgt. D. Williams, Parramatta LAC.',
    policeRef:'NSW Police Event #2026-44821',
    activity:[
      { time:'08:15:00', operator:'J. Chen', action:'Job created manually', note:'Request received via phone from Sgt. D. Williams, Parramatta Police Station.' },
      { time:'08:22:30', operator:'J. Chen', action:'Updated', note:'Footage located in Milestone XProtect. 58-min clip from CAM-040 and CAM-041.' },
      { time:'08:50:20', operator:'J. Chen', action:'Submitted for supervisor review', note:'Export package prepared in Milestone. Awaiting supervisor authorisation before releasing to police.' },
    ],
  },
  {
    id:'4', number:'JOB-0004', type:'Perimeter Breach', priority:'High',
    site:'Aquatic Centre', location:'Plant Room', status:'assigned',
    assignedTo:'M. Torres', createdBy:'System (Alarm Auto)', createdAt:'08:35:11', updatedAt:'08:37:00',
    description:'Motion detected in plant room. Area not authorised for access at this time of day.',
    linkedAlarmId:3,
    activity:[
      { time:'08:35:11', operator:'System', action:'Job auto-created from alarm', note:'Alarm #3 — Motion Detected, Aquatic Centre Plant Room' },
      { time:'08:37:00', operator:'J. Okafor', action:'Assigned to M. Torres', note:'Tasked with camera review and guard coordination.' },
    ],
  },
  {
    id:'5', number:'JOB-0005', type:'Technical Fault', priority:'Low',
    site:'River Walk', location:'Electrical Room', status:'pending_review',
    assignedTo:'J. Chen', createdBy:'System (Alarm Auto)', createdAt:'07:58:33', updatedAt:'08:05:00',
    description:'Power failure at River Walk. Camera RW-PATH-01 offline. Facilities notified.',
    linkedAlarmId:6,
    activity:[
      { time:'07:58:33', operator:'System', action:'Job auto-created from alarm', note:'Alarm #6 — Power Failure, River Walk Electrical Room' },
      { time:'08:00:10', operator:'J. Chen', action:'Alarm acknowledged', note:'Power failure confirmed via building management system.' },
      { time:'08:05:00', operator:'J. Chen', action:'Submitted for supervisor review', note:'Facilities work order WO-2026-8821 raised. Estimated restoration 10:00 AEST.' },
    ],
  },
  {
    id:'6', number:'JOB-0006', type:'Suspicious Person / Loitering', priority:'Medium',
    site:'Town Hall', location:'Entry Plaza', status:'pending_review',
    assignedTo:'J. Okafor', createdBy:'System (Alarm Auto)', createdAt:'07:42:10', updatedAt:'08:10:00',
    description:'Individual loitering — photographing building entrance. Guard approached. Individual left peacefully.',
    linkedAlarmId:7,
    activity:[
      { time:'07:42:10', operator:'System', action:'Job auto-created from alarm', note:'Alarm #7 — Loitering, Town Hall Entry Plaza' },
      { time:'07:45:00', operator:'J. Okafor', action:'Updated', note:'Male, approx 30–40yrs, dark jacket, backpack. Photographing entrance for approx 8 mins.' },
      { time:'07:52:00', operator:'J. Okafor', action:'Updated', note:'Guard J. Chen made contact. Individual identified as visiting architect — showed credentials.' },
      { time:'08:10:00', operator:'J. Okafor', action:'Submitted for supervisor review', note:'Individual departed north on Church St. No further action. Footage retained 30 days.' },
    ],
  },
];

// ─── New Job Modal ────────────────────────────────────────────────────────────

const BLANK = { type: JOB_TYPES[0], priority: 'High' as Priority, site: '', location: '', description: '', assignedTo: '', policeRef: '', linkedAssetId: '', linkedAssetName: '' };

function NewJobModal({ onSave, onClose, prefill, liveSites, liveAssets }: {
  onSave: (j: Job) => void;
  onClose: () => void;
  prefill?: Record<string,string>;
  liveSites: string[];
  liveAssets: { id: string; name: string; site: string; location: string }[];
}) {
  const siteList = liveSites.length > 0 ? liveSites : SITES;
  const assetOptions = [{ id:'', name:'— No Asset —' }, ...liveAssets.map(a => ({ id: a.id, name: `${a.id} — ${a.name} (${a.site})` }))];

  const [form, setForm] = useState(() => {
    const defaultSite = (prefill?.site && siteList.includes(prefill.site)) ? prefill.site : (siteList[0] || '');
    return {
      ...BLANK,
      type: (prefill?.type && JOB_TYPES.includes(prefill.type)) ? prefill.type : BLANK.type,
      priority: prefill?.sev === 'critical' ? 'Critical' as Priority : prefill?.sev === 'high' ? 'High' as Priority : BLANK.priority,
      site: defaultSite,
      location: prefill?.loc || BLANK.location,
      description: prefill?.alarm ? `Auto-created from Alarm #${prefill.alarm} — ${prefill.type || ''} at ${prefill.site || ''}, ${prefill.loc || ''}. Review camera feed and action SOP.` : BLANK.description,
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  function validate() {
    const e: Record<string, string> = {};
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.description.trim()) e.description = 'Description is required';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 700));
    const t = now();
    const job: Job = {
      id: String(Date.now()), number: nextJobNumber(),
      type: form.type, priority: form.priority, site: form.site,
      location: form.location, description: form.description,
      assignedTo: form.assignedTo || undefined,
      policeRef: form.policeRef || undefined,
      linkedAssetId: form.linkedAssetId || undefined,
      linkedAssetName: form.linkedAssetName || undefined,
      createdBy: 'Current User', createdAt: t, updatedAt: t,
      status: form.assignedTo ? 'assigned' : 'new',
      activity: [{ time: t, operator: 'Current User', action: 'Job created manually', note: form.description }],
    };
    setSaving(false); setSaved(true);
    onSave(job);
    setTimeout(onClose, 900);
  }

  const inp = (label: string, key: string, placeholder = '', full = false) => (
    <div style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'4px', fontWeight:500 }}>{label}</label>
      <input value={(form as any)[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder}
        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${errors[key]?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
      {errors[key] && <div style={{ fontSize:'10px', color:'#ef4444', marginTop:'3px' }}>{errors[key]}</div>}
    </div>
  );

  const sel = (label: string, key: string, opts: string[], full = false) => (
    <div style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'4px', fontWeight:500 }}>{label}</label>
      <select value={(form as any)[key]} onChange={e => set(key, e.target.value)}
        style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none' }}>
        {opts.map(o => <option key={o} style={{ background:'#111111' }}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', width:'580px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ margin:0, fontSize:'16px', fontWeight:700, color:'#e5e5e5' }}>Create New Job</h2>
            <p style={{ margin:'3px 0 0', fontSize:'11px', color:'#555555' }}>
              Manual entry — next number: <span style={{ color:'#3b82f6', fontFamily:'monospace', fontWeight:700 }}>{peekNextJobNumber()}</span>
            </p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'4px', color:'#555555', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          {sel('Job Type', 'type', JOB_TYPES, true)}
          {sel('Priority', 'priority', Array.from(PRIORITIES))}
          {/* Site — live from Assets module */}
          <div>
            <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'4px', fontWeight:500 }}>Site</label>
            <select value={form.site} onChange={e => set('site', e.target.value)}
              style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none' }}>
              {siteList.length === 0 && <option value="" style={{ background:'#111111' }}>— Add sites in Asset Management —</option>}
              {siteList.map(s => <option key={s} value={s} style={{ background:'#111111' }}>{s}</option>)}
            </select>
          </div>
          {inp('Location', 'location', 'e.g. Main Entry, Level 2, Car Park')}
          {sel('Assign To', 'assignedTo', ['— Unassigned —', ...OPERATORS])}
          {inp('Police / External Ref', 'policeRef', 'e.g. NSW Police Event #...')}
          {/* Asset — live from Assets module */}
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'4px', fontWeight:500 }}>Linked Asset (optional)</label>
            <select value={form.linkedAssetId} onChange={e => {
              const opt = assetOptions.find(a => a.id === e.target.value);
              setForm(f => ({ ...f, linkedAssetId: e.target.value, linkedAssetName: opt?.name || '' }));
            }} style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none' }}>
              {assetOptions.map(o => <option key={o.id} value={o.id} style={{ background:'#111111' }}>{o.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'4px', fontWeight:500 }}>Description *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Describe what was observed or reported..."
              style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${errors.description?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none', boxSizing:'border-box' as const, resize:'none' as const }} />
            {errors.description && <div style={{ fontSize:'10px', color:'#ef4444', marginTop:'3px' }}>{errors.description}</div>}
          </div>
        </div>
        <div style={{ padding:'14px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving||saved} style={{
            padding:'8px 24px', borderRadius:'4px', border:'none', fontSize:'12px', fontWeight:700, cursor:saving||saved?'default':'pointer',
            background: saved ? 'rgba(34,197,94,0.2)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
            color: saved ? '#22c55e' : 'white', transition:'all 0.2s',
          }}>
            {saved ? '✓ Job Created!' : saving ? 'Creating...' : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Email Preview Modal ──────────────────────────────────────────────────────

function EmailPreviewModal({ jobs, onClose }: { jobs: Job[]; onClose: () => void }) {
  const [sent, setSent] = useState(false);
  const active  = jobs.filter(j => j.status === 'in_progress' || j.status === 'assigned' || j.status === 'new');
  const pending = jobs.filter(j => j.status === 'pending_review');
  const closed  = jobs.filter(j => j.status === 'closed');
  const today   = new Date().toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', width:'680px', maxWidth:'95vw', maxHeight:'92vh', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'16px 24px', borderBottom:'1px solid #1a1a1a', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:0, fontSize:'14px', fontWeight:700, color:'#e5e5e5' }}>Morning Summary Email Preview</h3>
            <p style={{ margin:'2px 0 0', fontSize:'11px', color:'#555555' }}>To: manager@parramatta.nsw.gov.au</p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'4px', color:'#555555', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer' }}>✕</button>
        </div>
        {/* Email body preview */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <div style={{ background:'white', borderRadius:'4px', padding:'24px', color:'#111111', fontFamily:'Arial, sans-serif' }}>
            {/* Email header */}
            <div style={{ borderBottom:'3px solid #1d4ed8', paddingBottom:'16px', marginBottom:'20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontSize:'10px', color:'#555555', letterSpacing:'2px', fontWeight:700, marginBottom:'4px' }}>CITY OF PARRAMATTA COUNCIL</div>
                  <div style={{ fontSize:'20px', fontWeight:800, color:'#0a0a0a' }}>SOC Shift Summary</div>
                  <div style={{ fontSize:'12px', color:'#555555', marginTop:'4px' }}>{today} — Day Shift 06:00–14:00</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'10px', color:'#555555' }}>Generated by Lemai</div>
                  <div style={{ fontSize:'10px', color:'#555555' }}>{new Date().toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
              {[
                { l:'Total Jobs', v: String(jobs.length), c:'#1d4ed8' },
                { l:'Active', v: String(active.length), c:'#d97706' },
                { l:'Pending Review', v: String(pending.length), c:'#2563eb' },
                { l:'Closed', v: String(closed.length), c:'#16a34a' },
              ].map(s => (
                <div key={s.l} style={{ background:'#f8fafc', borderRadius:'4px', padding:'12px', textAlign:'center' }}>
                  <div style={{ fontSize:'22px', fontWeight:800, color:s.c }}>{s.v}</div>
                  <div style={{ fontSize:'10px', color:'#555555', marginTop:'2px' }}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Critical/High priority alert */}
            {jobs.filter(j=>j.priority==='Critical'&&j.status!=='closed').length > 0 && (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'4px', padding:'12px', marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:'#dc2626', marginBottom:'6px' }}>ACTION REQUIRED — Critical Open Jobs</div>
                {jobs.filter(j=>j.priority==='Critical'&&j.status!=='closed').map(j => (
                  <div key={j.id} style={{ fontSize:'11px', color:'#7f1d1d', marginBottom:'3px' }}>
                    [{j.number}] {j.type} — {j.site}, {j.location} ({j.status.replace('_',' ')})
                  </div>
                ))}
              </div>
            )}

            {/* Jobs table */}
            <div style={{ fontSize:'12px', fontWeight:700, color:'#0a0a0a', marginBottom:'8px' }}>All Jobs This Shift</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
              <thead>
                <tr style={{ background:'#f1f5f9' }}>
                  {['Job No.', 'Type', 'Priority', 'Site', 'Operator', 'Created', 'Status'].map(h => (
                    <th key={h} style={{ padding:'6px 8px', textAlign:'left', color:'#555555', fontWeight:600, borderBottom:'1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((j,i) => (
                  <tr key={j.id} style={{ background: i%2===0 ? 'white' : '#f8fafc' }}>
                    <td style={{ padding:'6px 8px', color:'#1d4ed8', fontWeight:600, borderBottom:'1px solid #f1f5f9' }}>{j.number}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9' }}>{j.type}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9', color: j.priority==='Critical'?'#dc2626':j.priority==='High'?'#d97706':'#374151', fontWeight:600 }}>{j.priority}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9' }}>{j.site}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9' }}>{j.assignedTo||'—'}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9', fontFamily:'monospace' }}>{j.createdAt}</td>
                    <td style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9', fontWeight:600, color: j.status==='closed'?'#16a34a':j.status==='pending_review'?'#2563eb':'#d97706' }}>{STATUS_LABEL[j.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer */}
            <div style={{ marginTop:'20px', paddingTop:'12px', borderTop:'1px solid #e2e8f0', fontSize:'10px', color:'#a0a0a0', textAlign:'center' }}>
              This report was auto-generated by Lemai Security Intelligence Platform · City of Parramatta Council SOC
            </div>
          </div>
        </div>
        {/* Send button */}
        <div style={{ padding:'14px 24px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Close</button>
          <button onClick={() => { window.location.href='mailto:?subject=Job%20Morning%20Summary%20%E2%80%94%20Lemai&body=Please%20review%20the%20attached%20morning%20summary%20in%20Lemai.'; onClose(); }} style={{
            padding:'8px 24px', borderRadius:'4px', border:'none', fontSize:'12px', fontWeight:700, cursor:'pointer',
            background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white',
          }}>
            EMAIL MANAGER
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Escalate to Incident Button ─────────────────────────────────────────────

function EscalateToIncidentButton({ job, onEscalate }: { job: Job; onEscalate: (job: Job, incNumber: string) => void }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [incNumber, setIncNumber] = useState('');

  async function handle() {
    setState('loading');
    const token = localStorage.getItem('lemai_token') || '';
    const t = new Date().toTimeString().slice(0, 8);
    const d = new Date();
    const prefix = `INC-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-`;
    const incNum = `${prefix}${String(Math.floor(Math.random()*900)+100)}`;
    const inc = {
      number:        incNum,
      type:          job.type,
      severity:      job.priority === 'Critical' ? 'critical' : job.priority === 'High' ? 'high' : job.priority === 'Medium' ? 'medium' : 'low',
      site:          job.site,
      location:      job.location,
      description:   `Escalated from job ${job.number}. ${job.description}`,
      assignedTo:    job.assignedTo,
      status:        'investigating',
      createdBy:     'Current User',
      createdAt:     t,
      linkedJobId:   job.id,
      timeline: [{ time: t, operator: 'Current User', action: 'Incident escalated from job', note: `Source: ${job.number} — ${job.type} at ${job.site}` }],
    };
    try {
      const res = await fetch('/api/incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(inc),
      });
      const saved = res.ok ? await res.json() : inc;
      setIncNumber(saved.number || incNum);
      setState('done');
      onEscalate(job, saved.number || incNum);
    } catch {
      setIncNumber(incNum);
      setState('done');
      onEscalate(job, incNum);
    }
  }

  if (state === 'loading') {
    return <div style={{ padding:'10px 14px', fontSize:'12px', color:'#555555', textAlign:'center' }}>Creating incident…</div>;
  }
  if (state === 'done') {
    return (
      <div style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', padding:'10px 14px', fontSize:'12px', color:'#fca5a5' }}>
        ✅ Escalated — <span style={{ fontWeight:700, fontFamily:'monospace' }}>{incNumber}</span> created and linked to {job.number}
      </div>
    );
  }
  return (
    <button onClick={handle} style={{ width:'100%', padding:'10px', borderRadius:'4px', border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#f87171', fontWeight:700, fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
      🚨 Escalate to Incident
    </button>
  );
}

// ─── Job Detail Panel ─────────────────────────────────────────────────────────

function JobDetail({ job, onUpdate, onClose, isSupervisor, onEscalate }: {
  job: Job;
  onUpdate: (id: string, patch: Partial<Job>, activityNote: string, action: string) => void;
  onClose: () => void;
  isSupervisor: boolean;
  onEscalate: (job: Job, incNumber: string) => void;
}) {
  const [note, setNote] = useState('');
  const [nextStatus, setNextStatus] = useState<JobStatus>(job.status);
  const [assignTo, setAssignTo] = useState(job.assignedTo || '');
  const [supNote, setSupNote] = useState(job.supervisorNotes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);

  async function handleNotify() {
    const tok = localStorage.getItem('lemai_token') || '';
    setNotifying(true);
    try {
      await fetch(`/api/jobs/${job.id}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({}),
      });
      setNotified(true);
      setTimeout(() => setNotified(false), 3000);
    } catch { /* silent */ } finally { setNotifying(false); }
  }

  // ── Cost items ──
  const [showCostEntry, setShowCostEntry] = useState(false);
  const [costDesc, setCostDesc] = useState('');
  const [costQty, setCostQty] = useState(1);
  const [costUnit, setCostUnit] = useState(0);
  const [labourDraft, setLabourDraft] = useState(job.labourHours || 0);

  function addCostItem() {
    if (!costDesc.trim()) return;
    const item: CostItem = { id: `CI-${Date.now()}`, description: costDesc.trim(), qty: costQty, unitCost: costUnit };
    onUpdate(job.id, { costItems: [...(job.costItems || []), item] }, `Added parts: ${item.description} ×${item.qty} ($${(item.qty*item.unitCost).toFixed(2)})`, 'Cost item added');
    setCostDesc(''); setCostQty(1); setCostUnit(0);
  }

  function saveLabour() {
    onUpdate(job.id, { labourHours: labourDraft }, `Labour hours updated: ${labourDraft}h`, 'Labour hours updated');
  }

  // ── Photo upload ──
  const [photos, setPhotos] = useState<{ name: string; url: string }[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch(`/api/jobs/${job.id}/photos`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.photos) setPhotos(d.photos); })
      .catch(() => {});
  }, [job.id]);

  async function handlePhotoUpload(file: File) {
    if (!file) return;
    setUploadingPhoto(true);
    const tok = localStorage.getItem('lemai_token') || '';
    const form = new FormData();
    form.append('photo', file);
    try {
      const r = await fetch(`/api/jobs/${job.id}/photos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
        body: form,
      });
      if (r.ok) {
        const d = await r.json();
        setPhotos(prev => [...prev, { name: d.filename || file.name, url: d.url || '' }]);
      }
    } catch {}
    setUploadingPhoto(false);
  }

  const operatorNextStatuses: JobStatus[] = job.status === 'new' ? ['new','assigned','in_progress']
    : job.status === 'assigned' ? ['assigned','in_progress','pending_review']
    : job.status === 'in_progress' ? ['in_progress','pending_review']
    : [job.status];

  const supervisorNextStatuses: JobStatus[] = ['new','assigned','in_progress','pending_review','closed'];

  const availableStatuses = isSupervisor ? supervisorNextStatuses : operatorNextStatuses;

  async function handleUpdate() {
    if (!note.trim()) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 500));
    onUpdate(job.id, { status: nextStatus, assignedTo: assignTo || undefined, supervisorNotes: isSupervisor ? supNote : job.supervisorNotes }, note, `Status → ${STATUS_LABEL[nextStatus]}`);
    setSaving(false); setSaved(true);
    setNote(''); setNextStatus(nextStatus);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflowY:'auto' }}>
      {/* Header */}
      <div style={{ padding:'16px 20px', borderBottom:'1px solid #1a1a1a' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px' }}>
          <div>
            <div style={{ fontSize:'11px', color:'#3b82f6', fontWeight:700, fontFamily:'monospace', marginBottom:'3px' }}>{job.number}</div>
            <div style={{ fontSize:'15px', fontWeight:700, color:'#e5e5e5' }}>{job.type}</div>
          </div>
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <div style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'4px', background:`${PRIORITY_COLOR[job.priority]}15`, color:PRIORITY_COLOR[job.priority] }}>{job.priority}</div>
            <div style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'4px', background:`${STATUS_COLOR[job.status]}15`, color:STATUS_COLOR[job.status] }}>{STATUS_LABEL[job.status]}</div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'6px', color:'#555555', width:'26px', height:'26px', fontSize:'14px', cursor:'pointer' }}>✕</button>
          </div>
        </div>
        <div style={{ fontSize:'11px', color:'#555555' }}>{job.site} · {job.location}</div>
        {job.assignedTo && (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'2px' }}>
            <span style={{ fontSize:'11px', color:'#555555' }}>Assigned: {job.assignedTo}</span>
            {job.status !== 'closed' && (
              <button onClick={handleNotify} disabled={notifying || notified}
                style={{ padding:'2px 8px', borderRadius:'5px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.08)', color: notified ? '#22c55e' : '#3b82f6', fontSize:'9px', fontWeight:700, cursor:'pointer', transition:'all 0.2s' }}>
                {notified ? '✓ Notified' : notifying ? 'Sending…' : '📧 Notify'}
              </button>
            )}
          </div>
        )}
        {job.policeRef && <div style={{ fontSize:'11px', color:'#f59e0b', marginTop:'2px' }}>Police Ref: {job.policeRef}</div>}
        {job.linkedAssetId && <div style={{ fontSize:'11px', color:'#a78bfa', marginTop:'2px' }}>🖥️ Asset: {job.linkedAssetName || job.linkedAssetId}</div>}
      </div>

      {/* Camera feed section */}
      {(() => {
        const cameras = SITE_CAMERAS[job.site] || [];
        if (!cameras.length) return null;
        const vmsUrl = localStorage.getItem('lemai_vms_url') || '';
        let camStreams: Record<string,string> = {};
        try { camStreams = JSON.parse(localStorage.getItem('lemai_cam_streams') || '{}'); } catch {}
        return (
          <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', background:'rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
              <div style={{ fontSize:'10px', color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                Linked Cameras — {job.site}
              </div>
              {vmsUrl && (
                <button onClick={() => window.open(vmsUrl, '_blank')}
                  style={{ padding:'2px 8px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'5px', color:'#3b82f6', fontSize:'9px', fontWeight:700, cursor:'pointer' }}>
                  Open VMS →
                </button>
              )}
            </div>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {cameras.slice(0, 4).map(cam => {
                const streamUrl = camStreams[cam] || '';
                return (
                  <div key={cam} style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'6px', padding:'4px 8px', display:'flex', alignItems:'center', gap:'5px' }}>
                    <div style={{ width:'5px', height:'5px', borderRadius:'50%', background: streamUrl ? '#22c55e' : '#555555', flexShrink:0 }} />
                    <span style={{ fontSize:'10px', color:'#93c5fd', fontFamily:'monospace', fontWeight:600 }}>{cam}</span>
                    {streamUrl && (
                      <a href={streamUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize:'9px', color:'#22c55e', textDecoration:'none', fontWeight:700 }}>Live →</a>
                    )}
                  </div>
                );
              })}
            </div>
            {job.linkedAlarmId && (
              <div style={{ marginTop:'6px', fontSize:'10px', color:'#555555' }}>
                Linked alarm: <span style={{ color:'#f59e0b', fontFamily:'monospace', fontWeight:600 }}>#{job.linkedAlarmId}</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Description */}
      <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', background:'rgba(255,255,255,0.01)' }}>
        <div style={{ fontSize:'10px', color:'#555555', marginBottom:'4px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Description</div>
        <div style={{ fontSize:'12px', color:'#a0a0a0', lineHeight:'1.5' }}>{job.description}</div>
      </div>

      {/* Parts & Cost tracking */}
      {(() => {
        const items = job.costItems || [];
        const partsTotal = items.reduce((s, c) => s + (c.qty * c.unitCost), 0);
        const labourRate = 85; // $85/hr default
        const labourTotal = (job.labourHours || 0) * labourRate;
        const grandTotal = partsTotal + labourTotal;
        if (items.length === 0 && !job.labourHours) return null;
        return (
          <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', background:'rgba(168,85,247,0.03)' }}>
            <div style={{ fontSize:'10px', color:'#a855f7', marginBottom:'8px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Parts & Cost</div>
            {items.map(c => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#a0a0a0', marginBottom:'4px' }}>
                <span>{c.description} × {c.qty}</span>
                <span style={{ fontFamily:'monospace' }}>${(c.qty * c.unitCost).toFixed(2)}</span>
              </div>
            ))}
            {job.labourHours ? (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#a0a0a0', marginBottom:'4px' }}>
                <span>Labour — {job.labourHours}h @ ${labourRate}/hr</span>
                <span style={{ fontFamily:'monospace' }}>${labourTotal.toFixed(2)}</span>
              </div>
            ) : null}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', fontWeight:700, color:'white', borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:'6px', marginTop:'6px' }}>
              <span>Total</span>
              <span style={{ fontFamily:'monospace', color:'#a855f7' }}>${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        );
      })()}

      {/* Activity log */}
      <div style={{ flex:1, padding:'12px 20px', overflowY:'auto' }}>
        <div style={{ fontSize:'10px', color:'#555555', marginBottom:'10px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Activity Log</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {job.activity.map((a, i) => (
            <div key={i} style={{ display:'flex', gap:'10px' }}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: i === 0 ? '#3b82f6' : 'rgba(255,255,255,0.15)', flexShrink:0, marginTop:'4px' }} />
                {i < job.activity.length - 1 && <div style={{ width:'1px', flex:1, background:'rgba(255,255,255,0.06)', marginTop:'4px' }} />}
              </div>
              <div style={{ flex:1, paddingBottom:'8px' }}>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'2px' }}>
                  <span style={{ fontSize:'11px', fontWeight:600, color:'#a0a0a0' }}>{a.operator}</span>
                  <span style={{ fontSize:'10px', color:'#444444', fontFamily:'monospace' }}>{a.time}</span>
                </div>
                <div style={{ fontSize:'11px', color:'#555555', fontStyle:'italic', marginBottom:'2px' }}>{a.action}</div>
                {a.note && <div style={{ fontSize:'11px', color:'#e5e5e5', lineHeight:'1.5', background:'#0d0d0d', borderRadius:'6px', padding:'6px 8px', borderLeft:'2px solid rgba(59,130,246,0.3)' }}>{a.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Update form */}
      {job.status !== 'closed' && (
        <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(255,255,255,0.01)' }}>
          <div style={{ fontSize:'10px', color:'#555555', marginBottom:'8px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Add Update</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            placeholder="Describe actions taken, observations, next steps..."
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const, resize:'none' as const, marginBottom:'8px' }} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Status</label>
              <select value={nextStatus} onChange={e => setNextStatus(e.target.value as JobStatus)}
                style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}>
                {availableStatuses.map(s => <option key={s} value={s} style={{ background:'#111111' }}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Assign To</label>
              <select value={assignTo} onChange={e => setAssignTo(e.target.value)}
                style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}>
                <option style={{ background:'#111111' }}>— Keep current —</option>
                {OPERATORS.map(o => <option key={o} style={{ background:'#111111' }}>{o}</option>)}
              </select>
            </div>
          </div>
          {isSupervisor && (
            <div style={{ marginBottom:'8px' }}>
              <label style={{ display:'block', fontSize:'10px', color:'#f59e0b', marginBottom:'4px' }}>Supervisor Notes</label>
              <textarea value={supNote} onChange={e => setSupNote(e.target.value)} rows={1}
                placeholder="Supervisor review notes..."
                style={{ width:'100%', background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'7px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const, resize:'none' as const }} />
            </div>
          )}
          {isSupervisor && (
            <div style={{ marginBottom:'8px' }}>
              <button onClick={() => setShowCostEntry(v=>!v)} style={{ fontSize:'10px', fontWeight:700, color:'#a855f7', background:'none', border:'none', cursor:'pointer', padding:0, textTransform:'uppercase', letterSpacing:'0.4px' }}>
                {showCostEntry ? '▲ Hide' : '▼ Add Parts & Labour'}
              </button>
              {showCostEntry && (
                <div style={{ marginTop:'8px', background:'rgba(168,85,247,0.04)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'4px', padding:'10px' }}>
                  <div style={{ fontSize:'10px', fontWeight:700, color:'#a855f7', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Parts & Cost</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 50px 80px auto', gap:'6px', marginBottom:'6px', alignItems:'flex-end' }}>
                    <div>
                      <label style={{ display:'block', fontSize:'9px', color:'#555555', marginBottom:'3px' }}>Description</label>
                      <input value={costDesc} onChange={e=>setCostDesc(e.target.value)} placeholder="e.g. Replacement lens cap"
                        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', padding:'4px 7px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:'9px', color:'#555555', marginBottom:'3px' }}>Qty</label>
                      <input type="number" min={1} value={costQty} onChange={e=>setCostQty(Number(e.target.value))}
                        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', padding:'4px 6px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:'9px', color:'#555555', marginBottom:'3px' }}>Unit Cost ($)</label>
                      <input type="number" min={0} step={0.01} value={costUnit} onChange={e=>setCostUnit(Number(e.target.value))}
                        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', padding:'4px 6px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
                    </div>
                    <button onClick={addCostItem} style={{ padding:'5px 10px', borderRadius:'5px', border:'none', background:'rgba(168,85,247,0.2)', color:'#c084fc', fontSize:'10px', fontWeight:700, cursor:'pointer', alignSelf:'flex-end' }}>+ Add</button>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'8px', alignItems:'center' }}>
                    <div>
                      <label style={{ display:'block', fontSize:'9px', color:'#555555', marginBottom:'3px' }}>Labour Hours</label>
                      <input type="number" min={0} step={0.5} value={labourDraft} onChange={e=>setLabourDraft(Number(e.target.value))}
                        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'5px', padding:'4px 7px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
                    </div>
                    <button onClick={saveLabour} style={{ padding:'5px 10px', borderRadius:'5px', border:'none', background:'rgba(168,85,247,0.2)', color:'#c084fc', fontSize:'10px', fontWeight:700, cursor:'pointer', marginTop:'12px' }}>Save</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ── One-click Close / Finalise (supervisor only, pending_review) ── */}
          {isSupervisor && job.status === 'pending_review' && (
            <div style={{ marginBottom:'8px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', padding:'10px 12px' }}>
              <div style={{ fontSize:'10px', color:'#22c55e', fontWeight:700, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Finalise Job</div>
              <div style={{ fontSize:'11px', color:'#555555', marginBottom:'8px' }}>Close this job as supervisor-approved. All activity will be locked and a closure record created.</div>
              <button
                onClick={() => {
                  const closeNote = supNote.trim() || 'Job reviewed and closed by supervisor.';
                  onUpdate(job.id, { status: 'closed', supervisorNotes: closeNote }, closeNote, 'Job closed by supervisor');
                }}
                style={{ width:'100%', padding:'9px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}
              >
                ✓ Close &amp; Finalise Job
              </button>
            </div>
          )}
          <button onClick={handleUpdate} disabled={!note.trim()||saving||saved} style={{
            width:'100%', padding:'8px', borderRadius:'4px', border:'none', fontSize:'12px', fontWeight:700,
            cursor: note.trim() && !saving && !saved ? 'pointer' : 'default',
            background: saved ? 'rgba(34,197,94,0.15)' : !note.trim() ? 'rgba(100,116,139,0.1)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
            color: saved ? '#22c55e' : !note.trim() ? '#222222' : 'white',
            transition:'all 0.2s',
          }}>
            {saved ? '✓ Updated' : saving ? 'Saving...' : nextStatus === 'pending_review' ? 'Submit for Supervisor Review' : nextStatus === 'closed' ? 'Close Job' : 'Save Update'}
          </button>
        </div>
      )}

      {job.status === 'closed' && (
        <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(34,197,94,0.04)' }}>
          <div style={{ fontSize:'11px', color:'#22c55e', fontWeight:600 }}>Job closed by supervisor</div>
          {job.supervisorNotes && <div style={{ fontSize:'11px', color:'#555555', marginTop:'4px' }}>{job.supervisorNotes}</div>}
        </div>
      )}

      {/* ── Photo Evidence ── */}
      <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
          <div style={{ fontSize:'11px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            Photo Evidence {photos.length > 0 && `(${photos.length})`}
          </div>
          {job.status !== 'closed' && (
            <>
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display:'none' }}
                onChange={e => { if (e.target.files?.[0]) handlePhotoUpload(e.target.files[0]); e.target.value=''; }} />
              <button onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}
                style={{ padding:'3px 10px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.08)', color:'#60a5fa', fontSize:'10px', fontWeight:700, cursor:'pointer' }}>
                {uploadingPhoto ? 'Uploading…' : '📷 Add Photo'}
              </button>
            </>
          )}
        </div>
        {photos.length === 0 && (
          <div style={{ fontSize:'11px', color:'#444444', textAlign:'center', padding:'12px 0' }}>No photos attached</div>
        )}
        {photos.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap' as const, gap:'8px' }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position:'relative' as const, width:'72px', height:'72px', borderRadius:'4px', overflow:'hidden', background:'#111111', border:'1px solid #1a1a1a', flexShrink:0 }}>
                {p.url ? (
                  <img src={p.url} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                ) : (
                  <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', color:'#555555' }}>📷</div>
                )}
                <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.6)', padding:'2px 4px' }}>
                  <div style={{ fontSize:'8px', color:'#a0a0a0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' as const }}>{p.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escalate to Incident */}
      {(job.priority === 'Critical' || job.priority === 'High') && job.status !== 'closed' && (
        <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
          <EscalateToIncidentButton job={job} onEscalate={onEscalate} />
        </div>
      )}
    </div>
  );
}

// ─── Morning Summary Tab ──────────────────────────────────────────────────────

function MorningSummary({ jobs, onEmail }: { jobs: Job[]; onEmail: () => void }) {
  const byType: Record<string, number> = {};
  jobs.forEach(j => { byType[j.type] = (byType[j.type] || 0) + 1; });
  const active  = jobs.filter(j => ['new','assigned','in_progress'].includes(j.status)).length;
  const pending = jobs.filter(j => j.status === 'pending_review').length;
  const closed  = jobs.filter(j => j.status === 'closed').length;

  return (
    <div style={{ padding:'20px' }}>
      {/* Shift header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <h3 style={{ margin:0, fontSize:'16px', fontWeight:700, color:'#e5e5e5' }}>
            Shift Summary — {new Date().toLocaleDateString('en-AU',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </h3>
          <p style={{ margin:'4px 0 0', fontSize:'12px', color:'#555555' }}>Day Shift 06:00–14:00 · City of Parramatta Council SOC</p>
        </div>
        <button onClick={onEmail} style={{ padding:'8px 18px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
          <span>✉</span> Email to Manager
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px', marginBottom:'20px' }}>
        {[
          { l:'Total Jobs', v: String(jobs.length), c:'#e2e8f0' },
          { l:'Critical', v: String(jobs.filter(j=>j.priority==='Critical').length), c:'#ef4444' },
          { l:'Active', v: String(active), c:'#f59e0b' },
          { l:'Pending Review', v: String(pending), c:'#3b82f6' },
          { l:'Closed', v: String(closed), c:'#22c55e' },
        ].map(s => (
          <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'12px', textAlign:'center' }}>
            <div style={{ fontSize:'24px', fontWeight:800, color:s.c }}>{s.v}</div>
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'2px' }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Breakdown by type */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px' }}>
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'16px' }}>
          <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e5e5e5' }}>Jobs by Type</h4>
          {Object.entries(byType).sort((a,b)=>b[1]-a[1]).map(([type, count]) => (
            <div key={type} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontSize:'11px', color:'#a0a0a0' }}>{type}</span>
              <span style={{ fontSize:'12px', fontWeight:700, color:'#e5e5e5' }}>{count}</span>
            </div>
          ))}
        </div>

        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'16px' }}>
          <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e5e5e5' }}>Outstanding — Action Required</h4>
          {jobs.filter(j=>j.status!=='closed').length === 0 ? (
            <div style={{ fontSize:'12px', color:'#22c55e' }}>All jobs closed</div>
          ) : jobs.filter(j=>j.status!=='closed').map(j => (
            <div key={j.id} style={{ marginBottom:'8px', padding:'8px', background:'#0d0d0d', borderRadius:'6px', borderLeft:`3px solid ${PRIORITY_COLOR[j.priority]}` }}>
              <div style={{ fontSize:'10px', color:PRIORITY_COLOR[j.priority], fontWeight:700 }}>{j.priority} · {STATUS_LABEL[j.status]}</div>
              <div style={{ fontSize:'11px', color:'#a0a0a0' }}>[{j.number}] {j.type} — {j.site}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Full job table */}
      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', overflow:'hidden' }}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'12px', fontWeight:700, color:'#e5e5e5' }}>All Jobs This Shift</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#0d0d0d' }}>
                {['Job No.','Type','Priority','Site','Location','Operator','Time','Status'].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:'10px', color:'#555555', fontWeight:600, letterSpacing:'0.5px', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#3b82f6', fontWeight:700, fontFamily:'monospace', whiteSpace:'nowrap' }}>{j.number}</td>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#e5e5e5', whiteSpace:'nowrap' }}>{j.type}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 6px', borderRadius:'6px', background:`${PRIORITY_COLOR[j.priority]}15`, color:PRIORITY_COLOR[j.priority] }}>{j.priority}</span>
                  </td>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#a0a0a0', whiteSpace:'nowrap' }}>{j.site}</td>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#555555', whiteSpace:'nowrap' }}>{j.location}</td>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#a0a0a0', whiteSpace:'nowrap' }}>{j.assignedTo || '—'}</td>
                  <td style={{ padding:'8px 12px', fontSize:'11px', color:'#555555', fontFamily:'monospace', whiteSpace:'nowrap' }}>{j.createdAt}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 6px', borderRadius:'6px', background:`${STATUS_COLOR[j.status]}15`, color:STATUS_COLOR[j.status] }}>{STATUS_LABEL[j.status]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Jobs Component ──────────────────────────────────────────────────────

type TabKey = 'active' | 'pending' | 'all' | 'closed' | 'summary' | 'tours' | 'handover' | 'recurring' | 'loneworker';

// ─── Lone Worker seed data ────────────────────────────────────────────────────

interface LWSession {
  id: string; name: string; role: string; site: string; location: string;
  startTime: string; lastCheckIn: string; nextDue: string; interval: number;
  status: 'active' | 'overdue' | 'distress' | 'completed';
  phone: string;
}

const LW_SESSIONS: LWSession[] = [];

// ─── Guard Tours seed data ────────────────────────────────────────────────────

const PATROL_ROUTES = [
  { id:'PR-01', name:'Town Hall Perimeter', site:'Town Hall', checkpoints:6, frequency:'Every 2 hours', lastStart:'06:00', nextDue:'08:00', officer:'J. Okafor', status:'in-progress' as const },
  { id:'PR-02', name:'Aquatic Centre Sweep', site:'Aquatic Centre', checkpoints:4, frequency:'Every 3 hours', lastStart:'07:30', nextDue:'10:30', officer:'S. Mitchell', status:'completed' as const },
  { id:'PR-03', name:'Civic Centre Nightly', site:'Civic Centre', checkpoints:8, frequency:'Nightly', lastStart:'22:00', nextDue:'22:00', officer:'M. Torres', status:'pending' as const },
  { id:'PR-04', name:'Depot Patrol', site:'Council Depot', checkpoints:5, frequency:'Every 4 hours', lastStart:'04:00', nextDue:'08:00', officer:'R. Patel', status:'overdue' as const },
];

// ─── Shift Handover seed data ─────────────────────────────────────────────────

const HANDOVER_ITEMS = [
  { id:1, type:'alarm',    priority:'critical', text:'Active duress alarm — Town Hall Control Room, pending supervisor response. Job JOB-20260410-0001 open.' },
  { id:2, type:'access',   priority:'high',     text:'Forced door — Depot Rear Gate, DOOR-05. Gate secured but not repaired. Facilities notified.' },
  { id:3, type:'system',   priority:'medium',   text:'Inner Range Integriti offline for 45 minutes from 06:45. Reconnected 07:30. No alarms missed per review.' },
  { id:4, type:'incident', priority:'medium',   text:'Suspicious person incident INC-1001 at Parramatta Square. Under investigation by J. Chen.' },
  { id:5, type:'job',      priority:'low',      text:'6 jobs closed this shift. 2 pending supervisor review (JOB-0002, JOB-0003). Morning summary emailed.' },
];

function elapsed(createdAt: string): string {
  const [h, m, s] = createdAt.split(':').map(Number);
  const now = new Date();
  const created = new Date();
  created.setHours(h || 0, m || 0, s || 0, 0);
  const diffMs = now.getTime() - created.getTime();
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins/60)}h ${mins%60}m ago`;
}

const API_TOKEN = () => localStorage.getItem('lemai_token') || '';

// ── Camera name lookup (mirrors SOCDashboard LINKED_CAMERAS) ──────────────────
const SITE_CAMERAS: Record<string, string[]> = {
  'Town Hall':          ['TH-ENTRY-01', 'TH-LOBBY-01', 'TH-ENTRY-02'],
  'Civic Centre':       ['CC-ENTRY-01', 'CC-ATRIUM-01'],
  'Aquatic Centre':     ['AQ-ENTRY-01', 'AQ-POOL-01'],
  'Council Depot':      ['DEP-ENTRY-01'],
  'Parramatta Square':  ['PS-ENTRY-01', 'PS-PLAZA-01'],
  'River Walk':         ['RW-PATH-01'],
};

// ─── Guard Roster ─────────────────────────────────────────────────────────────
type ShiftType = 'Day' | 'Night' | 'Graveyard' | 'Off';
const GUARD_ROSTER_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const GUARD_ROSTER_DATA: { name: string; shifts: ShiftType[] }[] = [
  { name: 'J. Okafor',   shifts: ['Day',      'Day',      'Night',     'Off',       'Day',      'Day',  'Night'    ] },
  { name: 'S. Mitchell', shifts: ['Night',     'Night',    'Off',       'Day',       'Day',      'Graveyard','Off'  ] },
  { name: 'J. Chen',     shifts: ['Off',       'Day',      'Day',       'Night',     'Night',    'Off',  'Day'      ] },
  { name: 'M. Torres',   shifts: ['Graveyard', 'Off',      'Day',       'Day',       'Off',      'Night','Night'    ] },
  { name: 'R. Patel',    shifts: ['Day',       'Graveyard','Off',       'Graveyard', 'Day',      'Day',  'Off'      ] },
];

function getShiftStyle(shift: ShiftType): React.CSSProperties {
  switch (shift) {
    case 'Day':       return { background: '#1e3a5f', color: '#93c5fd' };
    case 'Night':     return { background: '#422006', color: '#fed7aa' };
    case 'Graveyard': return { background: '#172554', color: '#818cf8' };
    case 'Off':       return { background: '#0a0a0a', color: '#444', border: '1px solid #1a1a1a' };
  }
}

function getTypeBadgeLabel(type: string): string {
  const l = type.toLowerCase();
  if (l.includes('patrol') || l.includes('perimeter') || l.includes('suspicious') || l.includes('trespass') || l.includes('loitering')) return 'PATROL';
  if (l.includes('duress') || l.includes('break') || l.includes('intrusion') || l.includes('medical') || l.includes('fire') || l.includes('damage') || l.includes('vehicle') || l.includes('welfare')) return 'INCIDENT';
  if (l.includes('technical') || l.includes('fault') || l.includes('maintenance') || l.includes('access control')) return 'MAINT';
  if (l.includes('footage') || l.includes('report') || l.includes('found') || l.includes('lost')) return 'REPORT';
  return 'INSPECT';
}

function getTypeBadgeStyle(type: string): React.CSSProperties {
  const l = type.toLowerCase();
  if (l.includes('patrol') || l.includes('perimeter') || l.includes('suspicious') || l.includes('trespass') || l.includes('loitering')) return { background: '#172554', color: '#93c5fd' };
  if (l.includes('duress') || l.includes('break') || l.includes('intrusion') || l.includes('medical') || l.includes('fire') || l.includes('damage') || l.includes('vehicle') || l.includes('welfare')) return { background: '#450a0a', color: '#fca5a5' };
  if (l.includes('technical') || l.includes('fault') || l.includes('maintenance') || l.includes('access control')) return { background: '#422006', color: '#fde047' };
  if (l.includes('footage') || l.includes('report') || l.includes('found') || l.includes('lost')) return { background: '#1e3a5f', color: '#93c5fd' };
  return { background: '#064e3b', color: '#6ee7b7' };
}

function getNewPriorityBadge(priority: Priority): React.ReactElement {
  const styles: Record<Priority, React.CSSProperties> = {
    Critical: { background: '#450a0a', color: '#fca5a5' },
    High:     { background: '#422006', color: '#fed7aa' },
    Medium:   { background: '#111', color: '#666', border: '1px solid #333' },
    Low:      { background: '#111', color: '#555', border: '1px solid #222' },
  };
  const label = priority === 'Critical' ? 'URGENT' : priority.toUpperCase();
  return <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' as const, padding: '1px 5px', ...styles[priority] }}>{label}</span>;
}

function getNewStatusLabel(status: JobStatus): string {
  switch (status) {
    case 'new':            return 'OPEN';
    case 'assigned':       return 'ASSIGNED';
    case 'in_progress':    return 'IN PROGRESS';
    case 'pending_review': return 'ON HOLD';
    case 'closed':         return 'COMPLETE';
    default:               return (status as string).toUpperCase();
  }
}

function getNewStatusBadge(status: JobStatus): React.ReactElement {
  const styles: Record<JobStatus, React.CSSProperties> = {
    new:            { background: '#052e16', color: '#86efac' },
    assigned:       { background: '#172554', color: '#93c5fd' },
    in_progress:    { background: '#1e3a5f', color: '#60a5fa' },
    pending_review: { background: '#1c1917', color: '#a8a29e', border: '1px solid #333' },
    closed:         { background: '#0a0a0a', color: '#555', border: '1px solid #222' },
  };
  return <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' as const, padding: '1px 5px', ...styles[status] }}>{getNewStatusLabel(status)}</span>;
}

export default function Jobs() {
  const { isDemoOrg } = useLicense();
  const [jobs, setJobs] = useState<Job[]>(isDemoOrg ? SEED_JOBS : []);
  const [selectedId, setSelectedId] = useState<string | null>(isDemoOrg ? '1' : null);
  const [initialLoading, setInitialLoading] = useState(!isDemoOrg);

  // Live sites and assets from Asset Management module
  const [liveSites, setLiveSites] = useState<string[]>(isDemoOrg ? SITES : []);
  const [liveAssets, setLiveAssets] = useState<{ id: string; name: string; site: string; location: string }[]>([]);

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    const hdr = { Authorization: `Bearer ${tok}` };
    // Load sites first, then equipment so we can resolve siteId → name
    fetch('/api/assets/sites', { headers: hdr })
      .then(r => r.ok ? r.json() : null)
      .then(sitesData => {
        const sites: { id: string; name: string }[] = sitesData?.sites || [];
        if (sites.length) setLiveSites(sites.map(s => s.name));
        const siteNameMap: Record<string, string> = {};
        sites.forEach(s => { siteNameMap[s.id] = s.name; });
        return fetch('/api/assets/equipment', { headers: hdr })
          .then(r => r.ok ? r.json() : null)
          .then(eqData => {
            if (eqData?.equipment?.length) {
              setLiveAssets(eqData.equipment.map((e: any) => ({
                id: e.id,
                name: e.name,
                site: siteNameMap[e.siteId] || e.siteId,
                location: e.location,
              })));
            }
          });
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showNewJob, setShowNewJob] = useState(false);
  // Pre-fill new job from alarm URL params (from SOC Dashboard "+ Job" button)
  const [alarmPrefill, setAlarmPrefill] = useState<Record<string,string>>({});
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const alarmId = params.get('alarm');
    if (alarmId) {
      setAlarmPrefill({
        alarm: alarmId,
        type: params.get('type') || '',
        site: params.get('site') || '',
        loc: params.get('loc') || '',
        sev: params.get('sev') || '',
      });
      setShowNewJob(true);  // open modal immediately
      // Clean URL
      window.history.replaceState({}, '', '/m/jobs');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJobs = useCallback(async () => {
    if (isDemoOrg) return;
    try {
      const res = await fetch('/api/jobs', { headers: { Authorization: `Bearer ${API_TOKEN()}` } });
      if (!res.ok) return;
      const data: Job[] = await res.json();
      syncJobSeq(data); // keep sequential counter in sync with loaded jobs
      setJobs(data);
    } catch {}
    setInitialLoading(false);
  }, [isDemoOrg]);

  useEffect(() => {
    fetchJobs();
    if (isDemoOrg) return;
    const poll = setInterval(fetchJobs, 10_000);
    return () => clearInterval(poll);
  }, [fetchJobs, isDemoOrg]);

  useEffect(() => {
    if (isDemoOrg) {
      setJobs(SEED_JOBS);
      setSelectedId('1');
    } else {
      setJobs([]);
      setSelectedId(null);
      setInitialLoading(true);
      fetchJobs();
    }
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps
  // Guard tours — live API data
  const [tours, setTours] = useState<any[]>([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [expandedTour, setExpandedTour] = useState<string | null>(null);
  const [completingCp, setCompletingCp] = useState<string | null>(null);

  const fetchTours = useCallback(async () => {
    setToursLoading(true);
    try {
      const r = await fetch('/api/tours', { headers: { Authorization: `Bearer ${API_TOKEN()}` } });
      if (r.ok) setTours(await r.json());
    } catch {}
    setToursLoading(false);
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const completeCheckpoint = async (tourId: string, cpId: string) => {
    setCompletingCp(cpId);
    try {
      const r = await fetch(`/api/tours/${tourId}/checkpoint/${cpId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${API_TOKEN()}` },
      });
      if (r.ok) { await fetchTours(); }
    } catch {}
    setCompletingCp(null);
  };

  // ── Recurring job templates ──
  const [recurringTemplates, setRecurringTemplates] = useState<any[]>([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [deletingTmpl, setDeletingTmpl] = useState<string | null>(null);
  const [newTmpl, setNewTmpl] = useState({ title:'', type:JOB_TYPES[0], site:SITES[0], location:'', priority:'Medium' as Priority, assignedTo:'', intervalHours:24, description:'' });

  const fetchRecurring = useCallback(async () => {
    setRecurringLoading(true);
    try {
      const r = await fetch('/api/jobs/recurring', { headers: { Authorization: `Bearer ${API_TOKEN()}` } });
      if (r.ok) setRecurringTemplates(await r.json());
    } catch {}
    setRecurringLoading(false);
  }, []);

  useEffect(() => { fetchRecurring(); }, [fetchRecurring]);

  const createTemplate = async () => {
    if (!newTmpl.title.trim()) return;
    try {
      const r = await fetch('/api/jobs/recurring', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN()}` },
        body: JSON.stringify(newTmpl),
      });
      if (r.ok) {
        await fetchRecurring();
        setShowNewTemplate(false);
        setNewTmpl({ title:'', type:JOB_TYPES[0], site:SITES[0], location:'', priority:'Medium', assignedTo:'', intervalHours:24, description:'' });
        toast('Recurring template created', 'success');
      }
    } catch { toast('Failed to create template', 'error'); }
  };

  const deleteTemplate = async (id: string) => {
    setDeletingTmpl(id);
    try {
      const r = await fetch(`/api/jobs/recurring/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${API_TOKEN()}` },
      });
      if (r.ok) { await fetchRecurring(); toast('Template deleted', 'success'); }
    } catch {}
    setDeletingTmpl(null);
  };

  const lwData = React.useMemo(() => [] as LWSession[], []);

  const [tab, setTab] = useState<TabKey>('active');
  const { editing, setEditing, slots, isVisible, orderedWidgets, toggle, moveUp, moveDown, reset: resetLayout } = usePageCustomizer('jobs', JOBS_WIDGETS);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortCol, setSortCol] = useState<'priority'|'createdAt'|''>('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

  function handleSort(col: 'priority'|'createdAt') {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  // ── Filter panel state ────────────────────────────────────────────────────
  const [filterDraft, setFilterDraft] = useState<{ statuses: Set<string>; priorities: Set<string>; assignees: Set<string> }>({ statuses: new Set(), priorities: new Set(), assignees: new Set() });
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
  const [filterPriorities, setFilterPriorities] = useState<Set<string>>(new Set());
  const [filterAssignees, setFilterAssignees] = useState<Set<string>>(new Set());
  const [rosterExpanded, setRosterExpanded] = useState(true);

  function applyFilters() {
    setFilterStatuses(new Set(filterDraft.statuses));
    setFilterPriorities(new Set(filterDraft.priorities));
    setFilterAssignees(new Set(filterDraft.assignees));
  }
  function clearFilters() {
    const empty = { statuses: new Set<string>(), priorities: new Set<string>(), assignees: new Set<string>() };
    setFilterDraft(empty);
    setFilterStatuses(new Set()); setFilterPriorities(new Set()); setFilterAssignees(new Set());
  }

  const [copiedId, setCopiedId] = useState<string|null>(null);
  function copyJobNumber(e: React.MouseEvent, number: string) {
    e.stopPropagation();
    navigator.clipboard.writeText(number).then(() => {
      setCopiedId(number);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  }
  const [showEmail, setShowEmail] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const { toast } = useToast();
  const { log: auditLog } = useAudit();

  const handleCreate = useCallback(async (job: Job) => {
    if (!isDemoOrg) {
      try {
        const res = await fetch('/api/jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN()}` },
          body: JSON.stringify(job),
        });
        if (res.ok) {
          const saved: Job = await res.json();
          setJobs(prev => [saved, ...prev]);
          setSelectedId(saved.id);
        } else {
          setJobs(prev => [job, ...prev]);
          setSelectedId(job.id);
        }
      } catch {
        setJobs(prev => [job, ...prev]);
        setSelectedId(job.id);
      }
    } else {
      setJobs(prev => [job, ...prev]);
      setSelectedId(job.id);
    }
    setTab('active');
    toast(`Job created: ${job.number}`, 'success');
    auditLog({ category: 'job', action: 'Job created', operator: job.createdBy || 'Current User', site: job.site, target: job.number, detail: job.description?.slice(0, 200), severity: 'info' });
  }, [isDemoOrg, toast, auditLog]);

  const handleEscalate = useCallback((job: Job, incNumber: string) => {
    auditLog({ category: 'incident', action: 'Job escalated to incident', operator: 'Current User', site: job.site, target: `${job.number} → ${incNumber}`, severity: 'warning' });
    toast(`${job.number} escalated — ${incNumber} created in Incidents`, 'warning');
  }, [toast, auditLog]);

  const handleUpdate = useCallback(async (id: string, patch: Partial<Job>, activityNote: string, action: string) => {
    const t = now();
    // Optimistic update
    setJobs(prev => prev.map(j => j.id === id ? {
      ...j, ...patch, updatedAt: t,
      activity: Array.from(j.activity).concat([{ time: t, operator: isSupervisor ? 'Supervisor' : 'Current User', action, note: activityNote }]),
    } : j));
    if (!isDemoOrg) {
      try {
        await fetch(`/api/jobs/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN()}` },
          body: JSON.stringify({ ...patch, activityNote, activityAction: action }),
        });
        // Auto-notify assignee when job is assigned/reassigned
        if (patch.assignedTo) {
          const prev = jobs.find(j => j.id === id);
          if (patch.assignedTo !== prev?.assignedTo) {
            fetch(`/api/jobs/${id}/notify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN()}` },
              body: JSON.stringify({}),
            }).catch(() => {});
          }
        }
      } catch {}
    }
    const jobRef = jobs.find(j => j.id === id);
    const auditAction = patch.status === 'closed' ? 'Job closed' : patch.status === 'pending_review' ? 'Job submitted for review' : action || 'Job updated';
    auditLog({ category: 'job', action: auditAction, operator: isSupervisor ? 'Supervisor' : 'Current User', site: jobRef?.site, target: jobRef?.number, detail: activityNote?.slice(0, 200), severity: patch.status === 'closed' ? 'info' : 'info' });
    if (patch.status === 'closed') toast('Job closed by supervisor', 'success');
    else if (patch.status === 'pending_review') toast('Job submitted for supervisor review', 'info');
    else toast('Job updated', 'success');
  }, [isDemoOrg, isSupervisor, toast, auditLog, jobs]);

  const PRIORITY_ORDER: Record<Priority, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
  const filteredJobs = jobs.filter(j => {
    const matchesTab = tab === 'active' ? ['new','assigned','in_progress'].includes(j.status)
      : tab === 'pending' ? j.status === 'pending_review'
      : tab === 'closed' ? j.status === 'closed'
      : tab === 'summary' ? true
      : true;
    const matchesType = typeFilter === 'All' || j.type === typeFilter;
    const matchesSearch = !search || j.number.toLowerCase().includes(search.toLowerCase()) || j.type.toLowerCase().includes(search.toLowerCase()) || j.site.toLowerCase().includes(search.toLowerCase()) || (j.assignedTo||'').toLowerCase().includes(search.toLowerCase());
    const matchesPanelStatus   = filterStatuses.size   === 0 || filterStatuses.has(j.status);
    const matchesPanelPriority = filterPriorities.size === 0 || filterPriorities.has(j.priority);
    const matchesPanelAssignee = filterAssignees.size  === 0 || filterAssignees.has(j.assignedTo || '');
    return matchesTab && matchesType && matchesSearch && matchesPanelStatus && matchesPanelPriority && matchesPanelAssignee;
  }).sort((a, b) => {
    if (!sortCol) return 0;
    if (sortCol === 'priority') {
      const diff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      return sortDir === 'asc' ? diff : -diff;
    }
    if (sortCol === 'createdAt') {
      const diff = a.createdAt.localeCompare(b.createdAt);
      return sortDir === 'asc' ? diff : -diff;
    }
    return 0;
  });

  const selectedJob = jobs.find(j => j.id === selectedId) || null;

  const activeCount  = jobs.filter(j=>['new','assigned','in_progress'].includes(j.status)).length;
  const pendingCount = jobs.filter(j=>j.status==='pending_review').length;
  const closedCount  = jobs.filter(j=>j.status==='closed').length;
  const criticalOpen = jobs.filter(j=>j.priority==='Critical'&&j.status!=='closed').length;

  const tabStyle = (t: TabKey): React.CSSProperties => ({
    padding:'8px 14px', border:'none', background:'none', cursor:'pointer', fontSize:'12px', fontWeight:600,
    color: tab === t ? '#e2e8f0' : '#555555',
    borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
    transition:'all 0.15s', whiteSpace:'nowrap',
  });

  const jobTypes = ['All', ...Array.from(new Set(jobs.map(j => j.type)))];

  return (
    <Layout title="Jobs" subtitle="SOC Job Queue">
      {editing && <CustomizerPanel widgets={JOBS_WIDGETS} slots={slots} toggle={toggle} moveUp={moveUp} moveDown={moveDown} reset={resetLayout} onClose={() => setEditing(false)} />}

      <div style={{ margin: '-20px', display: 'flex', flexDirection: 'column', background: '#0a0a0a', minHeight: 'calc(100vh - 140px)' }}>

      {/* ── PAGE HEADER 36px ──────────────────────────────────────────────── */}
      <div style={{ height: '36px', flexShrink: 0, background: '#0a0a0a', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#e5e5e5', letterSpacing: '1px' }}>JOBS</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs..."
            style={{ width: '180px', height: '24px', background: '#111', border: '1px solid #333', borderRadius: '2px', color: '#e5e5e5', fontSize: '10px', padding: '0 8px', outline: 'none' }} />
          <CustomizeButton editing={editing} setEditing={setEditing} />
          <button onClick={() => setIsSupervisor(v => !v)}
            style={{ height: '26px', padding: '0 10px', background: isSupervisor ? 'rgba(245,158,11,0.1)' : '#1a1a1a', border: `1px solid ${isSupervisor ? '#f59e0b' : '#333'}`, color: isSupervisor ? '#f59e0b' : '#888', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }}>
            {isSupervisor ? '★ SUPERVISOR' : 'OPERATOR'}
          </button>
          <button onClick={() => setShowNewJob(true)}
            style={{ height: '26px', padding: '0 10px', background: '#16a34a', border: 'none', color: '#fff', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }}>
            + NEW JOB
          </button>
          <button onClick={() => exportCSV(`jobs-${csvDateStamp()}`, ['ID','Number','Type','Priority','Site','Status','Assignee','Created'], filteredJobs.map((j: any) => [j.id,j.number,j.type,j.priority,j.site,j.status,j.assignedTo,j.createdAt]))}
            style={{ height: '26px', padding: '0 10px', background: '#1a1a1a', border: '1px solid #333', color: '#888', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px' }}>
            EXPORT
          </button>
        </div>
      </div>

      {/* ── BODY ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── LEFT FILTER PANEL 200px ────────────────────────────────────── */}
        <div style={{ width: '200px', flexShrink: 0, background: '#111', borderRight: '1px solid #222', overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* STATUS */}
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#555', letterSpacing: '0.5px', borderBottom: '1px solid #1a1a1a', paddingBottom: '4px', marginBottom: '6px' }}>STATUS</div>
            {([{ value:'new', label:'Open' },{ value:'assigned', label:'Assigned' },{ value:'in_progress', label:'In Progress' },{ value:'pending_review', label:'Pending Review' },{ value:'closed', label:'Closed' }] as { value: string; label: string }[]).map(({ value, label }) => {
              const checked = filterDraft.statuses.has(value);
              return (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '22px', cursor: 'pointer' }}
                  onClick={() => setFilterDraft(prev => { const next = new Set(prev.statuses); checked ? next.delete(value) : next.add(value); return { ...prev, statuses: next }; })}>
                  <div style={{ width: '12px', height: '12px', border: '1px solid #333', background: checked ? '#3b82f6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {checked && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><polyline points="1,4 4,7 9,1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: '10px', color: '#a0a0a0' }}>{label}</span>
                </label>
              );
            })}
          </div>
          {/* TYPE */}
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#555', letterSpacing: '0.5px', borderBottom: '1px solid #1a1a1a', paddingBottom: '4px', marginBottom: '6px' }}>TYPE</div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              style={{ width: '100%', height: '24px', background: '#0a0a0a', border: '1px solid #333', color: '#a0a0a0', fontSize: '10px', outline: 'none', padding: '0 4px' }}>
              {jobTypes.map(t => <option key={t} value={t} style={{ background: '#111' }}>{t}</option>)}
            </select>
          </div>
          {/* PRIORITY */}
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#555', letterSpacing: '0.5px', borderBottom: '1px solid #1a1a1a', paddingBottom: '4px', marginBottom: '6px' }}>PRIORITY</div>
            {Array.from(PRIORITIES).map(p => {
              const checked = filterDraft.priorities.has(p);
              return (
                <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '22px', cursor: 'pointer' }}
                  onClick={() => setFilterDraft(prev => { const next = new Set(prev.priorities); checked ? next.delete(p) : next.add(p); return { ...prev, priorities: next }; })}>
                  <div style={{ width: '12px', height: '12px', border: '1px solid #333', background: checked ? '#3b82f6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {checked && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><polyline points="1,4 4,7 9,1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: '10px', color: '#a0a0a0' }}>{p}</span>
                </label>
              );
            })}
          </div>
          {/* ASSIGNED TO */}
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#555', letterSpacing: '0.5px', borderBottom: '1px solid #1a1a1a', paddingBottom: '4px', marginBottom: '6px' }}>ASSIGNED TO</div>
            {OPERATORS.map(op => {
              const checked = filterDraft.assignees.has(op);
              return (
                <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '22px', cursor: 'pointer' }}
                  onClick={() => setFilterDraft(prev => { const next = new Set(prev.assignees); checked ? next.delete(op) : next.add(op); return { ...prev, assignees: next }; })}>
                  <div style={{ width: '12px', height: '12px', border: '1px solid #333', background: checked ? '#3b82f6' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {checked && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><polyline points="1,4 4,7 9,1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: '10px', color: '#a0a0a0' }}>{op}</span>
                </label>
              );
            })}
          </div>
          {/* Apply + Clear */}
          <div style={{ marginTop: 'auto', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button onClick={applyFilters}
              style={{ width: '100%', height: '26px', background: '#1e3a5f', border: '1px solid #3b82f6', color: '#3b82f6', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', letterSpacing: '0.5px' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#1e3a5f'; e.currentTarget.style.color = '#3b82f6'; }}>
              Apply Filters
            </button>
            <button onClick={clearFilters}
              style={{ width: '100%', background: 'none', border: 'none', color: '#555', fontSize: '9px', cursor: 'pointer', padding: '2px 0' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#888')}
              onMouseLeave={e => (e.currentTarget.style.color = '#555')}>
              Clear
            </button>
          </div>
        </div>

        {/* ── RIGHT CONTENT AREA ──────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Tab strip */}
          <div style={{ height: '36px', background: '#0a0a0a', borderBottom: '2px solid #222', display: 'flex', alignItems: 'flex-end', flexShrink: 0, overflowX: 'auto' }}>
            {orderedWidgets.filter(w => isVisible(w.id)).map(w => {
              const t = w.id as TabKey;
              const labels: Record<string, React.ReactNode> = {
                active:     <>ACTIVE {activeCount > 0 && <span style={{ marginLeft:'4px', background:'rgba(245,158,11,0.2)', color:'#f59e0b', padding:'0 4px', fontSize:'9px' }}>{activeCount}</span>}</>,
                pending:    <>PENDING {pendingCount > 0 && <span style={{ marginLeft:'4px', background:'rgba(59,130,246,0.2)', color:'#3b82f6', padding:'0 4px', fontSize:'9px' }}>{pendingCount}</span>}</>,
                all: 'ALL JOBS', closed: 'CLOSED', summary: 'SUMMARY', tours: 'GUARD TOURS', handover: 'HANDOVER', recurring: 'RECURRING',
                loneworker: <>LONE WORKER {lwData.filter(s=>s.status==='overdue'||s.status==='distress').length > 0 && <span style={{ marginLeft:'4px', background:'rgba(239,68,68,0.2)', color:'#ef4444', padding:'0 4px', fontSize:'9px' }}>{lwData.filter(s=>s.status==='overdue'||s.status==='distress').length}</span>}</>,
              };
              return (
                <button key={t} onClick={() => setTab(t)} style={{
                  height: '36px', padding: '0 14px', background: 'transparent', border: 'none',
                  borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
                  color: tab === t ? '#e5e5e5' : '#666', fontSize: '10px', fontWeight: 700,
                  textTransform: 'uppercase', cursor: 'pointer', letterSpacing: '0.5px',
                  position: 'relative', marginBottom: '-2px', whiteSpace: 'nowrap',
                }}
                  onMouseEnter={e => { if (tab !== t) e.currentTarget.style.color = '#a0a0a0'; }}
                  onMouseLeave={e => { if (tab !== t) e.currentTarget.style.color = '#666'; }}
                >{labels[t]}</button>
              );
            })}
          </div>

          {/* ── TAB CONTENT ──────────────────────────────────────────────── */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Summary */}
            {tab === 'summary' && <div style={{ flex: 1, overflowY: 'auto' }}><MorningSummary jobs={jobs} onEmail={() => setShowEmail(true)} /></div>}

            {/* Guard Tours */}
            {tab === 'tours' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '16px' }}>
                  {[{ l:'Active Patrols', v:String(tours.filter((r:any)=>r.status==='in-progress').length), c:'#22c55e' },{ l:'Overdue', v:String(tours.filter((r:any)=>r.status==='overdue').length), c:'#ef4444' },{ l:'Completed Today', v:String(tours.filter((r:any)=>r.status==='completed').length), c:'#3b82f6' },{ l:'Scheduled', v:String(tours.filter((r:any)=>r.status==='scheduled').length), c:'#555555' }].map(s=>(
                    <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'12px' }}><div style={{ fontSize:'22px', fontWeight:700, color:s.c }}>{s.v}</div><div style={{ fontSize:'10px', color:'#555555', marginTop:'3px' }}>{s.l}</div></div>
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'10px' }}>
                  <button onClick={fetchTours} style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', color:'#a0a0a0', fontSize:'11px', cursor:'pointer' }}>Refresh</button>
                </div>
                {toursLoading && <div style={{ textAlign:'center', padding:'30px', color:'#555555', fontSize:'12px' }}>Loading tours…</div>}
                {!toursLoading && tours.length === 0 && <div style={{ textAlign:'center', padding:'30px', color:'#444444', fontSize:'12px' }}>No guard tours found.</div>}
                <div style={{ display:'grid', gap:'10px' }}>
                  {tours.map((r:any) => {
                    const c = r.status==='in-progress'?'#22c55e':r.status==='completed'?'#3b82f6':r.status==='overdue'?'#ef4444':r.status==='scheduled'?'#a855f7':'#555555';
                    const isExp = expandedTour === r.id;
                    const cps: any[] = r.checkpoints || [];
                    return (
                      <div key={r.id} style={{ background:'#0d0d0d', border:`1px solid ${r.status==='overdue'?'rgba(239,68,68,0.25)':'rgba(255,255,255,0.06)'}`, borderRadius:'4px', overflow:'hidden' }}>
                        <div style={{ padding:'14px', cursor:'pointer' }} onClick={() => setExpandedTour(isExp ? null : r.id)}>
                          <div style={{ display:'flex', alignItems:'flex-start', gap:'14px', flexWrap:'wrap' }}>
                            <div style={{ flex:1, minWidth:'200px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                                <span style={{ fontSize:'13px', fontWeight:700, color:'#e5e5e5' }}>{r.site} — {r.officer}</span>
                                <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'7px', fontWeight:700, background:`${c}18`, color:c, textTransform:'uppercase' }}>{r.status.replace('-',' ')}</span>
                              </div>
                              <div style={{ fontSize:'11px', color:'#555555' }}>{cps.length} checkpoints · {cps.filter((cp:any)=>cp.completed).length}/{cps.length} complete</div>
                            </div>
                            <div style={{ display:'flex', gap:'6px', flexShrink:0, alignItems:'center' }}>
                              {cps.length > 0 && <div style={{ width:'80px', height:'6px', borderRadius:'3px', background:'rgba(255,255,255,0.1)', overflow:'hidden' }}><div style={{ height:'100%', width:`${r.completionPct||0}%`, background:c }} /></div>}
                              <span style={{ fontSize:'10px', color:c, fontWeight:600 }}>{r.completionPct||0}%</span>
                              <span style={{ fontSize:'12px', color:'#555555' }}>{isExp ? '▲' : '▼'}</span>
                            </div>
                          </div>
                        </div>
                        {isExp && (
                          <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', padding:'12px 14px', background:'rgba(0,0,0,0.15)' }}>
                            {cps.map((cp:any) => (
                              <div key={cp.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 8px', marginBottom:'4px', borderRadius:'7px', background:cp.completed?'rgba(34,197,94,0.06)':cp.overdue?'rgba(239,68,68,0.06)':'rgba(255,255,255,0.02)' }}>
                                <div style={{ width:'16px', height:'16px', borderRadius:'50%', border:`2px solid ${cp.completed?'#22c55e':cp.overdue?'#ef4444':'rgba(255,255,255,0.15)'}`, background:cp.completed?'#22c55e':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                  {cp.completed && <span style={{ fontSize:'9px', color:'white' }}>✓</span>}
                                </div>
                                <div style={{ flex:1 }}><span style={{ fontSize:'11px', color:cp.completed?'#22c55e':cp.overdue?'#f87171':'#e2e8f0', fontWeight:600 }}>{cp.name}</span><span style={{ fontSize:'10px', color:'#555555', marginLeft:'8px' }}>{cp.location}</span></div>
                                {!cp.completed && r.status !== 'completed' && (
                                  <button onClick={e=>{e.stopPropagation();completeCheckpoint(r.id,cp.id);}} disabled={completingCp===cp.id}
                                    style={{ padding:'3px 10px', borderRadius:'6px', border:'none', background:'rgba(34,197,94,0.15)', color:'#22c55e', fontSize:'10px', fontWeight:700, cursor:'pointer', flexShrink:0 }}>
                                    {completingCp===cp.id?'…':'Check In'}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shift Handover */}
            {tab === 'handover' && (
              <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
                <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', padding:'12px 16px', marginBottom:'16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                    <span style={{ fontSize:'13px', fontWeight:700, color:'#93c5fd' }}>Shift Handover Summary</span>
                    <span style={{ fontSize:'10px', color:'#555555' }}>Night Shift → Day Shift · {new Date().toLocaleDateString('en-AU')}</span>
                  </div>
                  <div style={{ fontSize:'11px', color:'#555555' }}>Outgoing: J. Okafor · Incoming: S. Mitchell · Supervisor: K. Murray</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
                  {HANDOVER_ITEMS.map(item => {
                    const c = item.priority==='critical'?'#ef4444':item.priority==='high'?'#f59e0b':item.priority==='medium'?'#3b82f6':'#555555';
                    return (
                      <div key={item.id} style={{ display:'flex', gap:'12px', padding:'12px 14px', background:'#0d0d0d', border:'1px solid #1a1a1a', borderLeft:`3px solid ${c}`, borderRadius:'4px' }}>
                        <span style={{ fontSize:'9px', padding:'3px 8px', borderRadius:'6px', fontWeight:700, background:`${c}18`, color:c, flexShrink:0, alignSelf:'flex-start', textTransform:'uppercase', marginTop:'1px' }}>{item.priority}</span>
                        <p style={{ margin:0, fontSize:'12px', color:'#a0a0a0', lineHeight:'1.5' }}>{item.text}</p>
                      </div>
                    );
                  })}
                </div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'14px' }}>
                  <div style={{ fontSize:'11px', fontWeight:600, color:'#a0a0a0', marginBottom:'8px' }}>Incoming Officer Notes</div>
                  <textarea rows={3} placeholder="Add any observations or notes before signing off..." style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e5e5e5', fontSize:'12px', outline:'none', boxSizing:'border-box' as const, resize:'none' as const, marginBottom:'10px' }} />
                  <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                    <button style={{ padding:'8px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Save Draft</button>
                    <button style={{ padding:'8px 22px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>✓ Sign Off Handover</button>
                  </div>
                </div>
              </div>
            )}

            {/* Recurring */}
            {tab === 'recurring' && (
              <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
                  <div><div style={{ fontSize:'14px', fontWeight:600, color:'#e5e5e5' }}>Recurring Job Templates</div><div style={{ fontSize:'11px', color:'#555555', marginTop:'2px' }}>Jobs that auto-create on a schedule</div></div>
                  <div style={{ display:'flex', gap:'8px' }}>
                    <button onClick={fetchRecurring} style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'#111111', color:'#a0a0a0', fontSize:'11px', cursor:'pointer' }}>Refresh</button>
                    <button onClick={()=>setShowNewTemplate(v=>!v)} style={{ padding:'5px 14px', borderRadius:'7px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>{showNewTemplate?'Cancel':'+ New Template'}</button>
                  </div>
                </div>
                {showNewTemplate && (
                  <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', padding:'16px', marginBottom:'16px' }}>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'#93c5fd', marginBottom:'12px' }}>New Recurring Template</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Title</label><input value={newTmpl.title} onChange={e=>setNewTmpl(p=>({...p,title:e.target.value}))} placeholder="e.g. Nightly Patrol" style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} /></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Job Type</label><select value={newTmpl.type} onChange={e=>setNewTmpl(p=>({...p,type:e.target.value}))} style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}>{JOB_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Site</label><select value={newTmpl.site} onChange={e=>setNewTmpl(p=>({...p,site:e.target.value}))} style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}>{SITES.map(s=><option key={s}>{s}</option>)}</select></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Location</label><input value={newTmpl.location} onChange={e=>setNewTmpl(p=>({...p,location:e.target.value}))} placeholder="e.g. Perimeter" style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} /></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Priority</label><select value={newTmpl.priority} onChange={e=>setNewTmpl(p=>({...p,priority:e.target.value as Priority}))} style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Repeat Every (hours)</label><input type="number" min={1} value={newTmpl.intervalHours} onChange={e=>setNewTmpl(p=>({...p,intervalHours:Number(e.target.value)}))} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} /></div>
                      <div><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Assign To</label><select value={newTmpl.assignedTo} onChange={e=>setNewTmpl(p=>({...p,assignedTo:e.target.value}))} style={{ width:'100%', background:'#111111', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none' }}><option value="">Unassigned</option>{OPERATORS.map(o=><option key={o}>{o}</option>)}</select></div>
                      <div style={{ gridColumn:'span 2' }}><label style={{ display:'block', fontSize:'10px', color:'#555555', marginBottom:'4px' }}>Description</label><input value={newTmpl.description} onChange={e=>setNewTmpl(p=>({...p,description:e.target.value}))} placeholder="Instructions..." style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e5e5e5', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} /></div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'flex-end' }}><button onClick={createTemplate} style={{ padding:'7px 20px', borderRadius:'7px', border:'none', background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>Create Template</button></div>
                  </div>
                )}
                {recurringLoading && <div style={{ textAlign:'center', padding:'30px', color:'#555555', fontSize:'12px' }}>Loading templates…</div>}
                {!recurringLoading && recurringTemplates.length === 0 && !showNewTemplate && <div style={{ textAlign:'center', padding:'48px', color:'#444444', fontSize:'12px' }}><div style={{ fontSize:'28px', marginBottom:'10px', opacity:0.3 }}>🔁</div><div>No recurring templates. Create templates for jobs that repeat on a schedule.</div></div>}
                <div style={{ display:'grid', gap:'8px' }}>
                  {recurringTemplates.map((t:any) => {
                    const pc = PRIORITY_COLOR[t.priority as Priority] || '#555555';
                    const nextMs = t.lastSpawnedAt ? (new Date(t.lastSpawnedAt).getTime()+(t.intervalHours||24)*3600*1000) : null;
                    const nextStr = nextMs ? new Date(nextMs).toLocaleString('en-AU',{hour:'2-digit',minute:'2-digit',day:'2-digit',month:'short'}) : 'On next scheduler run';
                    return (
                      <div key={t.id} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'14px', display:'flex', alignItems:'flex-start', gap:'14px' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px', flexWrap:'wrap' as const }}>
                            <span style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>{t.title}</span>
                            <span style={{ fontSize:'9px', padding:'1px 7px', borderRadius:'4px', fontWeight:700, background:`${pc}15`, color:pc }}>{t.priority}</span>
                            <span style={{ fontSize:'9px', padding:'1px 7px', borderRadius:'4px', fontWeight:700, background:'rgba(59,130,246,0.1)', color:'#60a5fa' }}>Every {t.intervalHours}h</span>
                          </div>
                          <div style={{ fontSize:'11px', color:'#555555' }}>{t.site}{t.location?` · ${t.location}`:''} · {t.type}</div>
                          <div style={{ fontSize:'10px', color:'#444444', marginTop:'4px' }}>Next spawn: {nextStr}</div>
                        </div>
                        <button onClick={()=>deleteTemplate(t.id)} disabled={deletingTmpl===t.id} style={{ padding:'4px 10px', borderRadius:'6px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', color:'#ef4444', fontSize:'10px', fontWeight:700, cursor:'pointer', flexShrink:0 }}>{deletingTmpl===t.id?'…':'Delete'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lone Worker */}
            {tab === 'loneworker' && (
              <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'16px' }}>
                  {[{ l:'Active Sessions', v:String(lwData.filter(s=>s.status==='active').length), c:'#22c55e' },{ l:'Overdue', v:String(lwData.filter(s=>s.status==='overdue').length), c:'#f59e0b' },{ l:'DISTRESS', v:String(lwData.filter(s=>s.status==='distress').length), c:'#ef4444' },{ l:'Completed', v:String(lwData.filter(s=>s.status==='completed').length), c:'#555555' }].map(s=>(
                    <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'12px' }}><div style={{ fontSize:'22px', fontWeight:700, color:s.c }}>{s.v}</div><div style={{ fontSize:'10px', color:'#555555', marginTop:'3px' }}>{s.l}</div></div>
                  ))}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {lwData.length === 0 && <div style={{ textAlign:'center', padding:'20px', fontSize:'11px', color:'#444' }}>NO ACTIVE LONE WORKER SESSIONS</div>}
                  {lwData.map(sess => {
                    const c = sess.status==='distress'?'#ef4444':sess.status==='overdue'?'#f59e0b':sess.status==='active'?'#22c55e':'#555555';
                    return (
                      <div key={sess.id} style={{ background:'#0d0d0d', border:`1px solid ${sess.status==='distress'?'rgba(239,68,68,0.4)':sess.status==='overdue'?'rgba(245,158,11,0.25)':'rgba(255,255,255,0.06)'}`, borderRadius:'5px', padding:'16px' }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:'16px', flexWrap:'wrap' }}>
                          <div style={{ flex:1, minWidth:'200px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}><span style={{ fontSize:'14px', fontWeight:700, color:'#e5e5e5' }}>{sess.name}</span><span style={{ fontSize:'9px', padding:'2px 9px', borderRadius:'7px', fontWeight:700, background:`${c}18`, color:c, textTransform:'uppercase' as const }}>{sess.status}</span></div>
                            <div style={{ fontSize:'11px', color:'#555555', marginBottom:'3px' }}>{sess.role} · {sess.site} · {sess.location}</div>
                            <div style={{ fontSize:'10px', color:'#555555' }}>Check-in every {sess.interval}min · Last: {sess.lastCheckIn} · Next due: <span style={{ color:c, fontWeight:600 }}>{sess.nextDue}</span></div>
                          </div>
                          <div style={{ display:'flex', gap:'6px', flexShrink:0, flexWrap:'wrap' as const }}>
                            {sess.status==='distress'&&<button style={{ padding:'6px 14px', borderRadius:'7px', border:'none', background:'rgba(239,68,68,0.2)', color:'#ef4444', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>🚨 Emergency Response</button>}
                            {(sess.status==='overdue'||sess.status==='distress')&&<button style={{ padding:'6px 14px', borderRadius:'7px', border:'1px solid rgba(245,158,11,0.3)', background:'rgba(245,158,11,0.1)', color:'#f59e0b', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>📞 Call {sess.phone}</button>}
                            <button style={{ padding:'6px 12px', borderRadius:'7px', border:'1px solid rgba(59,130,246,0.2)', background:'rgba(59,130,246,0.08)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>Manual Check-in</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── JOB LIST TABS: active / pending / all / closed ─────────── */}
            {(['active','pending','all','closed'] as TabKey[]).includes(tab) && (
              <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

                {/* Job Table */}
                <div style={{ flex:1, overflowY:'auto', overflowX:'auto' }}>
                  {/* Header */}
                  <div style={{ display:'grid', gridTemplateColumns:'28px 95px 80px 1fr 100px 80px 80px 100px 70px', background:'#111', borderBottom:'2px solid #333', height:'28px', alignItems:'center', position:'sticky', top:0, zIndex:5, minWidth:'760px' }}>
                    <div style={{ padding:'0 8px' }}><div style={{ width:'12px', height:'12px', border:'1px solid #333' }} /></div>
                    {['ID','TYPE','TITLE','ASSIGNED','DUE','PRIORITY','STATUS','ACTIONS'].map(h => (
                      <div key={h} style={{ fontSize:'10px', fontWeight:700, textTransform:'uppercase', color:'#666', padding:'0 8px', letterSpacing:'0.3px' }}>{h}</div>
                    ))}
                  </div>
                  {initialLoading && <div style={{ textAlign:'center', padding:'60px', color:'#333', fontSize:'10px', textTransform:'uppercase', letterSpacing:'2px' }}>LOADING JOBS…</div>}
                  {!initialLoading && filteredJobs.length === 0 && <div style={{ textAlign:'center', padding:'60px', color:'#333', fontSize:'11px', textTransform:'uppercase', letterSpacing:'2px' }}>NO JOBS FOUND</div>}
                  {!initialLoading && filteredJobs.map((job, idx) => {
                    const isClosed   = job.status === 'closed';
                    const isSelected = selectedId === job.id;
                    const baseRowBg  = idx % 2 === 0 ? '#0a0a0a' : '#0d0d0d';
                    const tc         = isClosed ? '#555' : '#e5e5e5';
                    const tc2        = isClosed ? '#444' : '#a0a0a0';
                    return (
                      <div key={job.id}
                        onClick={() => setSelectedId(isSelected ? null : job.id)}
                        style={{ display:'grid', gridTemplateColumns:'28px 95px 80px 1fr 100px 80px 80px 100px 70px', height:'28px', alignItems:'center', background:isSelected?'#1e3a5f':baseRowBg, cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.025)', minWidth:'760px' }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#1e3a5f'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = baseRowBg; }}
                      >
                        <div style={{ padding:'0 8px' }}><div style={{ width:'12px', height:'12px', border:'1px solid #333', background:'transparent' }} /></div>
                        <div style={{ padding:'0 8px', fontSize:'10px', fontFamily:'monospace', color:isClosed?'#555':'#3b82f6', fontWeight:700 }}>{job.number}</div>
                        <div style={{ padding:'0 8px' }}><span style={{ fontSize:'9px', fontWeight:700, textTransform:'uppercase', padding:'1px 5px', ...getTypeBadgeStyle(job.type) }}>{getTypeBadgeLabel(job.type)}</span></div>
                        <div style={{ padding:'0 8px', fontSize:'11px', fontWeight:700, color:tc, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{job.type} <span style={{ fontWeight:400, color:tc2 }}>· {job.site}</span></div>
                        <div style={{ padding:'0 8px', fontSize:'10px', color:tc2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{job.assignedTo||'—'}</div>
                        <div style={{ padding:'0 8px', fontSize:'10px', fontFamily:'monospace', color:tc2 }}>{job.updatedAt}</div>
                        <div style={{ padding:'0 8px' }}>{getNewPriorityBadge(job.priority)}</div>
                        <div style={{ padding:'0 8px' }}>{getNewStatusBadge(job.status)}</div>
                        <div style={{ padding:'0 6px', display:'flex', gap:'3px' }} onClick={e => e.stopPropagation()}>
                          <button title="View" onClick={() => setSelectedId(job.id)}
                            style={{ width:'18px', height:'18px', background:'transparent', border:'none', color:'#555', cursor:'pointer', fontSize:'10px', display:'flex', alignItems:'center', justifyContent:'center' }}
                            onMouseEnter={e=>(e.currentTarget.style.color='#e5e5e5')} onMouseLeave={e=>(e.currentTarget.style.color='#555')}>▦</button>
                          <button title="Copy ID" onClick={e => copyJobNumber(e, job.number)}
                            style={{ width:'18px', height:'18px', background:'transparent', border:'none', color:'#555', cursor:'pointer', fontSize:'10px', display:'flex', alignItems:'center', justifyContent:'center' }}
                            onMouseEnter={e=>(e.currentTarget.style.color='#e5e5e5')} onMouseLeave={e=>(e.currentTarget.style.color='#555')}>⊕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Job Detail Sliding Panel 420px */}
                {selectedJob && (
                  <div style={{ width:'420px', flexShrink:0, background:'#111', borderLeft:'1px solid #333', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                    {/* Panel header */}
                    <div style={{ height:'40px', borderBottom:'1px solid #222', display:'flex', alignItems:'center', padding:'0 12px', gap:'8px', flexShrink:0 }}>
                      <span style={{ fontSize:'10px', fontFamily:'monospace', fontWeight:700, color:'#3b82f6' }}>{selectedJob.number}</span>
                      <span style={{ fontSize:'9px', fontWeight:700, textTransform:'uppercase', padding:'1px 5px', ...getTypeBadgeStyle(selectedJob.type) }}>{getTypeBadgeLabel(selectedJob.type)}</span>
                      {getNewStatusBadge(selectedJob.status)}
                      <button onClick={() => setSelectedId(null)}
                        style={{ marginLeft:'auto', background:'none', border:'none', color:'#666', fontSize:'18px', cursor:'pointer', width:'24px', height:'24px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                        onMouseEnter={e=>(e.currentTarget.style.color='#e5e5e5')} onMouseLeave={e=>(e.currentTarget.style.color='#666')}>×</button>
                    </div>
                    {/* JobDetail */}
                    <div style={{ flex:1, overflow:'hidden' }}>
                      <JobDetail key={selectedJob.id} job={selectedJob} onUpdate={handleUpdate} onClose={() => setSelectedId(null)} isSupervisor={isSupervisor} onEscalate={handleEscalate} />
                    </div>
                    {/* Bottom action bar */}
                    <div style={{ height:'40px', background:'#111', borderTop:'1px solid #222', display:'flex', alignItems:'center', padding:'0 8px', gap:'6px', flexShrink:0 }}>
                      {selectedJob.status !== 'closed' && (
                        <div style={{ flex:1, height:'28px', background:'#1e3a5f', border:'1px solid #3b82f6', borderRadius:'2px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:700, textTransform:'uppercase', color:'#3b82f6', letterSpacing:'0.5px' }}>
                          UPDATE STATUS
                        </div>
                      )}
                      <button onClick={() => setSelectedId(null)}
                        style={{ flex:1, height:'28px', background:'#111', border:'1px solid #333', borderRadius:'2px', color:'#888', fontSize:'10px', fontWeight:700, textTransform:'uppercase', cursor:'pointer', letterSpacing:'0.5px' }}
                        onMouseEnter={e=>(e.currentTarget.style.background='#1a1a1a')} onMouseLeave={e=>(e.currentTarget.style.background='#111')}>
                        CLOSE PANEL
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>{/* end TAB CONTENT */}
        </div>{/* end RIGHT CONTENT AREA */}
      </div>{/* end BODY */}

      {/* ── GUARD ROSTER (collapsible) ───────────────────────────────── */}
      <div style={{ flexShrink:0, borderTop:'1px solid #222' }}>
        <div style={{ height:'32px', background:'#111', display:'flex', alignItems:'center', padding:'0 12px', gap:'8px', cursor:'pointer', borderBottom:'1px solid #222' }}
          onClick={() => setRosterExpanded(v => !v)}>
          <span style={{ fontSize:'10px', fontWeight:700, textTransform:'uppercase', color:'#888', letterSpacing:'0.5px' }}>GUARD ROSTER</span>
          <span style={{ marginLeft:'auto', fontSize:'10px', color:'#555' }}>{rosterExpanded ? '▼' : '▶'}</span>
        </div>
        {rosterExpanded && (
          <div style={{ overflowX:'auto', background:'#0a0a0a' }}>
            <div style={{ display:'grid', gridTemplateColumns:'120px repeat(7,1fr)', borderBottom:'1px solid #1a1a1a', minWidth:'560px' }}>
              <div style={{ height:'28px' }} />
              {GUARD_ROSTER_DAYS.map(d => (
                <div key={d} style={{ height:'28px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, textTransform:'uppercase', color:'#555', borderLeft:'1px solid #1a1a1a' }}>{d}</div>
              ))}
            </div>
            {GUARD_ROSTER_DATA.map(guard => (
              <div key={guard.name} style={{ display:'grid', gridTemplateColumns:'120px repeat(7,1fr)', borderBottom:'1px solid #1a1a1a', minWidth:'560px' }}>
                <div style={{ height:'32px', display:'flex', alignItems:'center', padding:'0 8px', fontSize:'10px', color:'#a0a0a0' }}>{guard.name}</div>
                {guard.shifts.map((shift, i) => (
                  <div key={i} style={{ height:'32px', display:'flex', alignItems:'center', justifyContent:'center', borderLeft:'1px solid #1a1a1a', ...getShiftStyle(shift) }}>
                    <span style={{ fontSize:'9px', fontWeight:700 }}>{shift}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      </div>{/* end outer margin:-20px wrapper */}

      {showNewJob && <NewJobModal onSave={handleCreate} onClose={() => { setShowNewJob(false); setAlarmPrefill({}); }} prefill={alarmPrefill} liveSites={liveSites} liveAssets={liveAssets} />}
      {showEmail && <EmailPreviewModal jobs={jobs} onClose={() => setShowEmail(false)} />}
    </Layout>
  );
}
